import React from "react"
import { HashRouter } from "react-router-dom"
import classNames from "classnames"
import Container from "react-bootstrap/Container"
import Navbar from "react-bootstrap/Navbar"

import { AddToastMessage } from "../../api/types"
import AppContext from "../../components/AppContext"
import Environment from "../../environment"

import Header from "./components/Header/Header"
import Sidebar from "./components/Sidebar"
import Panel from "./components/Panel/Panel"
import Toast, { ToastProps } from "./components/Toast"
import BreadCrumbs from "./components/BreadCrumbs"
import RouterSwitch from "./components/RouterSwitch"
import Footer from "./components/Footer"

import styles from "./App.module.scss"

interface AppState {
  sidebarOpen: boolean
  renderPanelContent?: () => React.ReactNode
  renderModalContent?: () => React.ReactNode
  toasts: ToastProps[]
  toastCount: number
}

class App extends React.Component<{}, AppState> {
  constructor(props: {}) {
    super(props)
    this.state = {
      sidebarOpen: false,
      renderPanelContent: undefined,
      toasts: [],
      toastCount: 0
    }
  }

  public render() {
    const sidebarStyle = this.state.sidebarOpen ? "sidebar-open" : "sidebar-closed"
    const year = new Date().getFullYear()
    const renderToasts = this.state.toasts.slice(-5).map((toast) => <Toast key={"toast_" + toast.id} {...toast} />)

    return (
      <HashRouter>
        <AppContext.Provider
          value={{
            setPanelContent: this.renderPanelContent,
            useToasts: {
              addToast: this.addToast
            }
          }}
        >
          {Environment.showWarning && (
            <Navbar expand="md" bg="warning">
              <h3 className="mb-0 mx-auto">
                This is a website in progress, data stored here can be deleted without notice!
              </h3>
            </Navbar>
          )}

          <Header sidebarToggle={this.sidebarToggle} />

          <div className={classNames("modus-body", sidebarStyle)}>
            <Sidebar />
            <div className="modus-content-rows">
              <div className="modus-content-columns">
                <div className="modus-content">
                  <Container fluid={true} className="d-flex flex-column h-100">
                    <BreadCrumbs />
                    <RouterSwitch />
                  </Container>
                </div>
                <Panel renderPanelContent={this.state.renderPanelContent} />
              </div>
            </div>{" "}
          </div>

          <div className={styles.toastContainer}>{renderToasts}</div>

          <Footer year={year} />
        </AppContext.Provider>
      </HashRouter>
    )
  }

  private sidebarToggle = () =>
    this.setState((prevState) => ({
      sidebarOpen: !prevState.sidebarOpen
    }))

  private renderPanelContent = (renderPanelContent?: () => React.ReactNode) =>
    this.setState({
      renderPanelContent
    })

  private addToast = (
    message: AddToastMessage | string,
    type: "primary" | "danger" | "warning" | "success",
    autoClose?: boolean
  ) => {
    const toastId = this.state.toastCount + 1
    const onClose = () => this.removeToast(toastId)

    if (typeof message === "object" && (message.title || message.message)) {
      const title = message.title ? message.title.toString() : message.message ? message.message.toString() : ""
      const messages: string[] = []
      if (message.errors) {
        Object.keys(message.errors).forEach((k) => {
          const errs = message.errors![k] as string[]
          errs.forEach((err) => messages.push(`${k}: ${err}`))
        })
      } else if (message.detail) {
        messages.push(message.detail)
      }
      this.setState((prevState) => ({
        toasts: [
          ...prevState.toasts,
          {
            id: toastId,
            type,
            title,
            messages,
            autoClose,
            onClose
          }
        ],
        toastCount: toastId
      }))
    } else {
      this.setState((prevState) => ({
        toasts: [
          ...prevState.toasts,
          {
            id: toastId,
            type,
            title: message.toString(),
            messages: [],
            autoClose,
            onClose
          }
        ],
        toastCount: toastId
      }))
    }
  }

  private removeToast = (id: number) =>
    this.setState((prevState) => ({
      toasts: prevState.toasts.filter((toast) => toast.id !== id)
    }))
}

export default App
